import {api, RequestParam} from "@/shared/api";

import {BaseRecording, BaseRecordingConfig} from "./baseRecording";
import {WorkingTimeDetailsDialog} from "@/features/workingtime/workingTimeDetailsDialog";
import {WorkingTimeRequestDialog} from "@/features/requests/workingTimeRequestDialog";
import {WorkingTimeDateSpanDialog} from "@/features/workingtime/workingTimeDateSpanDialog";
import {WorkingTimeTable} from "@/features/workingtime/workingTimeTable";
import {WorkingTimeStatistics} from "@/features/workingtime/workingTimeStatistics";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {RecordDetailsDialog} from "@/shared/recording/recordDetailsDialog";
import {RecordDialog} from "@/shared/recording/recordDialog";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {
	getWorkingTimeTypesForAddAbsence,
	getWorkingTimeTypesForAddWorkingTime,
	isWorkingTimeDateTimeChangeAllowed
} from "@/shared/securityHelper";
import * as RecordingValidationUtil from "@/shared/validations/recordingValidationUtil";
import {buildValidationsListItem} from "@/shared/validations/validationsListComponent";
import {WorkingTimeStatusPanel} from "@/features/workingtime/workingTimeStatusPanel";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {mergeProperties} from "@/shared/utils/misc";
import {mapWorkingTimeToReportWorkingTime} from "@/shared/utils/recordMapper";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {ReportWorkingTime, WorkingTime, WorkingTimeStatus, WorkingTimeType} from "@/shared/timr";
import {getISODate, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {RecordedEntryStatus} from "@/shared/timrTypes";

export interface WorkingTimeRecordingConfig extends BaseRecordingConfig<WorkingTimeStatus> {
	workingTimeTypes: WorkingTimeType[];
	workingTimeTypesStatusPanel: WorkingTimeType[];
	breakTimeTypesStatusPanel: WorkingTimeType[];
	isGroupLeaderForThemself: boolean;
}

export class WorkingTimeRecordingPage extends BaseRecording<ReportWorkingTime, WorkingTimeStatus, WorkingTime> {
	private static WORKING_TIME_TABLE_PAGE_LENGTH = BaseRecording.RECORDING_TABLE_PAGE_LENGTH;

	declare private workingTimeStatistics: WorkingTimeStatistics;
	private workingTimeRequestDialog: WorkingTimeRequestDialog;
	private workingTimeDateSpanDialog: WorkingTimeDateSpanDialog;

	constructor(public config: WorkingTimeRecordingConfig) {
		super(config);
	}

	protected init() {
		super.init();
		this.recordingDetailsDialog = this.createRecordingDetailsDialog();

		if (globals.validateWorkingTime || globals.validateWorkingTimeOverlaps) {
			this.initValidationsListeners();
		}

		this.workingTimeStatistics = new WorkingTimeStatistics($("#workingTimeStatistics"));

		$(".addButton").click(() => {
			this.addRecording(this.statusPanel.getLastRecording());
		}).toggleClass("disabled", getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes, false).length === 0);

		$(".addAbsenceButton").click(() => {
			this.addAbsence(this.statusPanel.getLastRecording());
		}).toggleClass("disabled", getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes, false).length === 0);

		$(this.recordingTable).on(WorkingTimeTable.EVENT_EDIT_DATE_SPAN, (e, workingTime: ReportWorkingTime) => {
			this.editWorkingTimeDateSpan(workingTime);
		});
	}

	protected showRecording(workingTime: ReportWorkingTime) {
		if (workingTime.hasWorkingTimeRequest) {
			api("requests/findWorkingTimeRequestByWorkingTimeId.json")
				.get({workingTimeId: workingTime.id})
				.done((data) => this.getWorkingTimeRequestDetailsDialog().show(data))
				// If not found, show the working time details
				.fail(() => this.recordingDetailsDialog.show(workingTime));
		} else if (workingTime.workingTimeDateSpanId !== null) {
			api("workingTimeDateSpans/findWorkingTimeDateSpanWithValidationsByWorkingTimeId.json")
				.get({workingTimeId: workingTime.id})
				.done((response) => this.getWorkingTimeDateSpanDetailsDialog().show(response.workingTimeDateSpan, buildValidationsListItem(workingTime.id, response.validations, true)))
				// If not found, show working time details dialog
				.fail(() => this.recordingDetailsDialog.show(workingTime));
		} else {
			this.recordingDetailsDialog.show(workingTime);
		}
	}

	protected getRecordById(recordId: number, link: JQuery) {
		return this.getWorkingTimeById(recordId, link);
	}

	protected getWorkingTimeById(workingTimeRecordId: number, link: JQuery) {
		RecordingValidationUtil.setRecordingValidationLinkIconLoading(link);
		api("reports/getWorkingTime.json")
			.get({workingTimeId: workingTimeRecordId})
			.done((data) => {
				if (this.recordingTable.recordingChangeable(data)) {
					this.editRecording(data);
				} else {
					this.showRecording(data);
				}
				RecordingValidationUtil.resetRecordingValidationLinkIconLoading(link);
			});
	}

	protected createStatusPanel(): WorkingTimeStatusPanel {
		return new WorkingTimeStatusPanel({
			panel: $("#workingTimeStatusPanel"),
			fetchStatusURL: "recording/getWorkingTimeStatus.json",
			customFieldDefinitions: this.config.customFieldDefinitions,
			workingTimeTypes: this.config.workingTimeTypesStatusPanel,
			breakTimeTypes: this.config.breakTimeTypesStatusPanel
		});
	}

	protected createRecordingTable(): RecordingTable<ReportWorkingTime> {
		return new WorkingTimeTable({
			dataUrl: WorkingTimeTable.RECORDING_WORKING_TIMES_URL,
			element: $("#workingTimeTable"),
			initialFilter: this.buildFilterParams(),
			allowQuickSwitch: true,
			allowResume: true,
			allowSelect: false,
			linkToReport: "workingTime",
			customFieldDefinitions: this.config.customFieldDefinitions,
			statusNumbers: this.config.statusNumbers,
			allowedUserIds: this.config.allowedUserIds,
			showUser: false,
			orderable: false,
			clampNote: true,
			allowExpand: true,
			pageLength: WorkingTimeRecordingPage.WORKING_TIME_TABLE_PAGE_LENGTH,
			paging: false,
			excludeElementsDblclickEvent: [".quickSwitchButtonContainer"]
		});
	}

	protected createRecordingDetailsDialog(): RecordDetailsDialog<ReportWorkingTime> {
		return new WorkingTimeDetailsDialog({
			title: messages["workingTime.details.header"],
			dialogId: "#workingTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitions,
			editRecord: (recording: ReportWorkingTime) => this.editRecording(recording)
		});
	}

	private editWorkingTimeDateSpan(workingTime: ReportWorkingTime) {
		api("workingTimeDateSpans/findWorkingTimeDateSpanWithValidationsByWorkingTimeId.json")
			.get({workingTimeId: workingTime.id})
			.done((response) => this.getWorkingTimeDateSpanDetailsDialog().edit(response.workingTimeDateSpan, buildValidationsListItem(workingTime.id, response.validations, true)))
			// If not found, show the working time details
			.fail(() => this.recordingDetailsDialog.show(workingTime));
	}

	private getWorkingTimeRequestDetailsDialog(): WorkingTimeRequestDialog {
		if (this.workingTimeRequestDialog == null) {
			this.workingTimeRequestDialog = this.createWorkingTimeRequestDialog();
		}
		return this.workingTimeRequestDialog;
	}

	private createWorkingTimeRequestDialog(): WorkingTimeRequestDialog {
		const dialog = new WorkingTimeRequestDialog({
			dialogId: "#workingTimeRequestDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			customFieldDefinitions: this.config.customFieldDefinitions
		});
		$(dialog).on(WorkingTimeRequestDialog.EVENT_REQUEST_EDITED, () => {
			this.reloadTable();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_STATUS_CHANGED, () => {
			this.reloadTable();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_DELETED, () => {
			this.reloadTable();
		});
		return dialog;
	}

	private getWorkingTimeDateSpanDetailsDialog(): WorkingTimeDateSpanDialog {
		if (this.workingTimeDateSpanDialog == null) {
			this.workingTimeDateSpanDialog = this.createWorkingTimeDateSpanDialog();
		}
		return this.workingTimeDateSpanDialog;
	}

	private createWorkingTimeDateSpanDialog(): WorkingTimeDateSpanDialog {
		const dialog = new WorkingTimeDateSpanDialog({
			dialogId: "#workingTimeDateSpanDialog",
			workingTimeTypes: this.config.workingTimeTypes
		});
		$(dialog).on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_EDITED, () => {
			this.reloadTable();
		}).on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_DELETED, () => {
			this.reloadTable();
		});
		return dialog;
	}

	protected createRecordingDialog(): RecordDialog<ReportWorkingTime> {
		return new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			users: [],
			userSelectionEnabled: false,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
	}

	protected validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddWorkingTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.workingtime.title",
				message: "account.expired.workingtime",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	protected addRecording(lastRecording: WorkingTime | null) {
		if (!this.validateSecurityStatus()) {
			return;
		}
		Analytics.trackEvent("Working Time - Add Working Time", "Working Time");
		const workingTimeTypes = getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes, false);
		const workingTimeDialog = this.recordingDialog as WorkingTimeDialog;
		const defaultWorkingTimeType = workingTimeTypes.length > 0 ? workingTimeTypes[0] : null;
		const lastReportWorkingTime = lastRecording ? mergeProperties(mapWorkingTimeToReportWorkingTime(lastRecording), {
			workingTimeTypeId: defaultWorkingTimeType?.id
		}) : null;
		workingTimeDialog.create(lastReportWorkingTime, false, workingTimeTypes);
	}

	protected resumeRecording(recording: ReportWorkingTime) {
		if (recording.status === RecordedEntryStatus.Changeable) {
			api("recording/resumeWorkingTime.json")
				.post({
					id: recording.id,
					timestamp: Date.now()
				})
				.done(() => {
					this.reloadTable();
				});
		}
	}

	protected updateStatistics(status: WorkingTimeStatus) {
		this.workingTimeStatistics.updateStatistics(status);
	}

	protected buildFilterParams(): RequestParam[] {
		const endDate = Temporal.Now.plainDateISO();
		const startDate = endDate.subtract({years: 1});
		return [
			...super.buildFilterParams(),
			{name: "endDate", value: getISODate(endDate)},
			{name: "startDate", value: getISODate(startDate)},
		];
	}

	protected isDateTimeChangeAllowed(): boolean {
		return isWorkingTimeDateTimeChangeAllowed();
	}

	protected addAbsence(lastRecording: WorkingTime | null) {
		if (!this.validateSecurityStatus()) {
			return;
		}
		Analytics.trackEvent("Working Time - Add Vacation", "Working Time");
		const workingTimeTypes = getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes, false);
		const workingTimeDialog = this.recordingDialog as WorkingTimeDialog;
		const lastReportWorkingTime = lastRecording ? mergeProperties(mapWorkingTimeToReportWorkingTime(lastRecording), {
			workingTimeTypeId: null
		}) : null;
		workingTimeDialog.create(lastReportWorkingTime, false, workingTimeTypes);
	}
}
